import get from 'lodash/get'

import type { Contact } from 'types/list'

/**
 * Checks if tags include any of the specified values.
 *
 * Performs case-insensitive matching to determine if any of the provided
 * values exist within the tags array. Currently only supports 'any' match type.
 *
 * @param tags - Array of tag strings to search within
 * @param values - Array of values to search for
 * @param matchType - Type of matching to perform (currently only 'any' is supported)
 * @returns True if any value is found in tags, false otherwise
 *
 * @example
 * ```typescript
 * const tags = ['rock', 'alternative', 'indie']
 * const searchValues = ['ROCK', 'jazz']
 *
 * const hasMatch = areTagsIncludeValues(tags, searchValues, 'any')
 * // true - 'ROCK' matches 'rock' (case-insensitive)
 *
 * const noMatch = areTagsIncludeValues(tags, ['jazz', 'classical'], 'any')
 * // false - neither 'jazz' nor 'classical' found in tags
 * ```
 */
export const areTagsIncludeValues = (
  tags: string[],
  values: string[],
  matchType: 'any',
): boolean => {
  for (let i = 0; i < values.length; i++) {
    if (matchType === 'any') {
      const value = values[i]
      const result = tags
        .map(t => t.toLowerCase().trim())
        .includes(value.trim().toLowerCase())
      if (result) return result
    }
  }
  return false
}

const emptyArray: Array<string> = []

/**
 * Gets tags that are only partially selected across a collection of contacts.
 *
 * Identifies tags that exist on some but not all contacts in the provided array.
 * Useful for UI components that need to show partially selected states in tag filters.
 * Handles both current tags and pending changes.
 *
 * @param array - Array of contact objects to analyze
 * @returns Array of tag names that are partially selected, or empty array if none
 *
 * @example
 * ```typescript
 * const contacts = [
 *   { tags: ['vip', 'customer'] },
 *   { tags: ['customer', 'new'] },
 *   { tags: ['vip'] }
 * ]
 *
 * const partialTags = getPartiallySelectedTags(contacts)
 * // ['vip', 'new'] - these tags don't appear on all contacts
 * // 'customer' is not included because it appears on 2/3 contacts
 *
 * // With pending changes
 * const contactsWithChanges = [
 *   { tags: ['old'], pendingChanges: { tags: ['new', 'updated'] } },
 *   { tags: ['old'], pendingChanges: { tags: ['updated'] } }
 * ]
 *
 * const partialChanges = getPartiallySelectedTags(contactsWithChanges)
 * // ['new'] - 'updated' appears on all, 'new' only on first contact
 * ```
 */
export const getPartiallySelectedTags = (array: Contact[]): string[] => {
  const tags: string[] = array.flatMap(contact => {
    const tagsValue = get(contact, 'pendingChanges.tags', contact.tags || [])
    return tagsValue
  })

  const counts: {
    [key: string]: number
  } = {}
  tags.forEach(tag => {
    counts[tag] = (counts[tag] || 0) + 1
  })

  const result = Object.keys(counts).filter(
    (key: string) => (counts[key] ?? 0) < array.length,
  )
  if (result.length > 0) return result
  return emptyArray
}
